import React, { useState, useContext } from 'react';
import { Button, Badge, Modal, Form, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import { downloadDocument } from '../../utils/documentUtils';
import { FaDownload, FaEnvelope, FaTrash, FaLink, FaEdit } from 'react-icons/fa';
import './ChangeOrders.css';

const ChangeOrderList = ({ changeOrders, onRefresh, projectId, token, project, openEmailModal }) => {
  const [loadingAction, setLoadingAction] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingChangeOrder, setEditingChangeOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({
    description: '',
    amount: '',
    paymentDueDate: '',
    status: ''
  });

  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    return `$${parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { variant: 'secondary', label: 'Draft' },
      sent: { variant: 'info', label: 'Sent for Signature' },
      approved: { variant: 'success', label: 'Approved' },
      rejected: { variant: 'danger', label: 'Rejected' },
      voided: { variant: 'dark', label: 'Voided' }
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge bg={config.variant}>{config.label}</Badge>;
  };

  const getPaymentStatusBadge = (status) => {
    if (status === 'paid') {
      return <Badge bg="success">Paid</Badge>;
    }
    return <Badge bg="warning" text="dark">Pending</Badge>;
  };

  // Find the document ID for this change order from the project documents
  const findDocumentId = (changeOrder) => {
    if (!project?.documents) return null;
    const doc = project.documents.find(d => 
      d.DocumentType === 'change_order' && 
      d.DocumentName.includes(changeOrder.ChangeOrderNumber)
    );
    return doc?.DocumentId;
  };

  const handleDownload = async (changeOrder) => {
    const documentId = findDocumentId(changeOrder);
    if (documentId) {
      try {
        downloadDocument(documentId, `Change_Order_${changeOrder.ChangeOrderNumber}.docx`, token);
      } catch (error) {
        console.error('Error downloading document:', error);
        toast.error('Failed to download document');
      }
    } else {
      toast.error('Document not found');
    }
  };

  const handleEmail = (changeOrder) => {
    const documentId = findDocumentId(changeOrder);
    if (documentId && openEmailModal) {
      const doc = project.documents.find(d => d.DocumentId === documentId);
      if (doc) {
        openEmailModal(doc);
      }
    } else {
      toast.error('Document not found');
    }
  };

  const handleCreateSigningLink = async (changeOrderId) => {
    setLoadingAction(changeOrderId);
    try {
      const response = await fetch(`/api/change-orders/${changeOrderId}/signing-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({ expiresInDays: 7 })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create signing link');
      }

      const data = await response.json();
      
      // Show success message with signing link (same style as combined proposal)
      const LinkMessage = () => (
        <div>
          <p className="mb-2"><strong>Change Order {data.changeOrderNumber} Ready!</strong></p>
          <p className="mb-2">🔗 Signing link ready for client:</p>
          <div className="mt-2">
            <input 
              type="text" 
              value={data.signingUrl} 
              readOnly 
              style={{ 
                width: '100%', 
                padding: '8px',
                marginBottom: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '12px'
              }}
              onClick={(e) => e.target.select()}
            />
            <button 
              style={{
                padding: '6px 12px',
                backgroundColor: '#0d6efd',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onClick={() => {
                navigator.clipboard.writeText(data.signingUrl);
                toast.info('Link copied to clipboard!');
              }}
            >
              📋 Copy Link
            </button>
          </div>
        </div>
      );
      
      toast.success(<LinkMessage />, { autoClose: false });
      
      onRefresh();
    } catch (error) {
      console.error('Error creating signing link:', error);
      toast.error(error.message || 'Failed to create signing link');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleEdit = (changeOrder) => {
    setEditingChangeOrder(changeOrder);
    setEditFormData({
      description: changeOrder.Description || '',
      amount: changeOrder.Amount ? changeOrder.Amount.toString() : '',
      paymentDueDate: changeOrder.PaymentDueDate ? new Date(changeOrder.PaymentDueDate).toISOString().split('T')[0] : '',
      status: changeOrder.Status || 'draft'
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveEdit = async () => {
    if (!editingChangeOrder) return;

    setLoadingAction(editingChangeOrder.ChangeOrderId);
    try {
      const response = await fetch(`/api/change-orders/${editingChangeOrder.ChangeOrderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          description: editFormData.description,
          amount: parseFloat(editFormData.amount),
          paymentDueDate: editFormData.paymentDueDate || null,
          status: editFormData.status
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update change order');
      }

      const data = await response.json();
      
      // If document was regenerated, show the new signing link
      if (data.documentRegenerated && data.signingUrl) {
        const LinkMessage = () => (
          <div>
            <p className="mb-2"><strong>Change Order Updated!</strong></p>
            <p className="mb-2">A new document has been generated and the status has been reset to Draft.</p>
            <p className="mb-2">🔗 New signing link for client:</p>
            <div className="mt-2">
              <input 
                type="text" 
                value={data.signingUrl} 
                readOnly 
                style={{ 
                  width: '100%', 
                  padding: '8px',
                  marginBottom: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
                onClick={(e) => e.target.select()}
              />
              <button 
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#0d6efd',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onClick={() => {
                  navigator.clipboard.writeText(data.signingUrl);
                  toast.info('Link copied to clipboard!');
                }}
              >
                📋 Copy Link
              </button>
            </div>
          </div>
        );
        
        toast.success(<LinkMessage />, { autoClose: false });
      } else {
        toast.success(data.message || 'Change order updated successfully');
      }
      
      setShowEditModal(false);
      setEditingChangeOrder(null);
      onRefresh();
    } catch (error) {
      console.error('Error updating change order:', error);
      toast.error(error.message || 'Failed to update change order');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDelete = async (changeOrderId, changeOrderNumber, status) => {
    let confirmMessage = `Are you sure you want to delete ${changeOrderNumber}? This cannot be undone.`;
    
    if (status === 'approved') {
      confirmMessage = `WARNING: ${changeOrderNumber} has been approved by the client. Deleting it will remove it from invoices and payment tracking.\n\nAre you sure you want to delete this change order?`;
    } else if (status === 'sent') {
      confirmMessage = `WARNING: ${changeOrderNumber} has been sent for signature. The signing link will no longer work.\n\nAre you sure you want to delete this change order?`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoadingAction(changeOrderId);
    try {
      const response = await fetch(`/api/change-orders/${changeOrderId}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token }
      });

      if (!response.ok) {
        throw new Error('Failed to delete change order');
      }

      toast.success('Change order deleted successfully');
      onRefresh();
    } catch (error) {
      console.error('Error deleting change order:', error);
      toast.error('Failed to delete change order');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleVoid = async (changeOrderId, changeOrderNumber) => {
    if (!window.confirm(`Are you sure you want to void ${changeOrderNumber}? This will mark it as voided but keep it for records.`)) {
      return;
    }

    setLoadingAction(changeOrderId);
    try {
      const response = await fetch(`/api/change-orders/${changeOrderId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({ status: 'voided' })
      });

      if (!response.ok) {
        throw new Error('Failed to void change order');
      }

      toast.success('Change order voided successfully');
      onRefresh();
    } catch (error) {
      console.error('Error voiding change order:', error);
      toast.error('Failed to void change order');
    } finally {
      setLoadingAction(null);
    }
  };

  if (!changeOrders || changeOrders.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <p>No change orders for this project yet.</p>
      </div>
    );
  }

  return (
    <div className="change-order-list">
      {changeOrders.map(co => (
        <div key={co.ChangeOrderId} className={`change-order-item ${co.Status === 'voided' ? 'voided' : ''}`}>
          <div className="change-order-header">
            <div>
              <span className="change-order-number">{co.ChangeOrderNumber}</span>
              <span className="ms-2">{getStatusBadge(co.Status)}</span>
              <span className="ms-2">{getPaymentStatusBadge(co.PaymentStatus)}</span>
            </div>
            <div className="change-order-amount">
              {formatCurrency(co.Amount)}
            </div>
          </div>
          
          <div className="change-order-description">
            {co.Description}
          </div>
          
          <div className="change-order-meta">
            <span>Created: {formatDate(co.CreatedAt)}</span>
            {co.PaymentDueDate && (
              <span>Payment Due: {formatDate(co.PaymentDueDate)}</span>
            )}
            {co.ApprovedAt && (
              <span>Approved: {formatDate(co.ApprovedAt)} by {co.ApprovedBy}</span>
            )}
          </div>

          <div className="change-order-actions">
            {/* Download Button */}
            <Button 
              variant="outline-primary" 
              size="sm"
              className="me-2"
              onClick={() => handleDownload(co)}
            >
              <FaDownload className="me-1" /> Download
            </Button>
            
            {/* Email Button */}
            <Button
              variant="outline-success"
              size="sm"
              className="me-2"
              onClick={() => handleEmail(co)}
              disabled={!project?.ClientEmail}
              title={!project?.ClientEmail ? "Client has no email address" : "Send to client"}
            >
              <FaEnvelope className="me-1" /> Email
            </Button>
            
            {/* Signing Link Button - only for draft or sent status */}
            {(co.Status === 'draft' || co.Status === 'sent') && (
              <Button
                variant="outline-info"
                size="sm"
                className="me-2"
                onClick={() => handleCreateSigningLink(co.ChangeOrderId)}
                disabled={loadingAction === co.ChangeOrderId}
              >
                <FaLink className="me-1" />
                {loadingAction === co.ChangeOrderId ? 'Creating...' : 'Get Signing Link'}
              </Button>
            )}

            {/* Edit Button - available for all non-voided statuses */}
            {co.Status !== 'voided' && (
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-2"
                onClick={() => handleEdit(co)}
                disabled={loadingAction === co.ChangeOrderId}
              >
                <FaEdit className="me-1" /> Edit
              </Button>
            )}

            {/* Void Button - for approved change orders that shouldn't be deleted */}
            {co.Status === 'approved' && co.PaymentStatus !== 'paid' && (
              <Button
                variant="outline-warning"
                size="sm"
                className="me-2"
                onClick={() => handleVoid(co.ChangeOrderId, co.ChangeOrderNumber)}
                disabled={loadingAction === co.ChangeOrderId}
              >
                Void
              </Button>
            )}

            {/* Delete Button - available for all non-paid statuses */}
            {co.PaymentStatus !== 'paid' && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => handleDelete(co.ChangeOrderId, co.ChangeOrderNumber, co.Status)}
                disabled={loadingAction === co.ChangeOrderId}
              >
                <FaTrash className="me-1" /> Delete
              </Button>
            )}
          </div>
        </div>
      ))}

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Change Order {editingChangeOrder?.ChangeOrderNumber}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                name="description"
                value={editFormData.description}
                onChange={handleEditFormChange}
                placeholder="Describe the change order..."
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Amount ($)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="amount"
                    value={editFormData.amount}
                    onChange={handleEditFormChange}
                    placeholder="0.00"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="paymentDueDate"
                    value={editFormData.paymentDueDate}
                    onChange={handleEditFormChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={editFormData.status}
                    onChange={handleEditFormChange}
                    disabled={editFormData.description !== editingChangeOrder?.Description || 
                              parseFloat(editFormData.amount) !== parseFloat(editingChangeOrder?.Amount)}
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent for Signature</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="voided">Voided</option>
                  </Form.Select>
                  {(editFormData.description !== editingChangeOrder?.Description || 
                    parseFloat(editFormData.amount) !== parseFloat(editingChangeOrder?.Amount)) && (
                    <Form.Text className="text-warning">
                      Status will reset to Draft when description or amount changes
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {editingChangeOrder?.Status === 'approved' && (
              <div className="alert alert-warning">
                <strong>Warning:</strong> This change order has been approved by the client. 
                Changing the description or amount will generate a new document and require the client to sign again.
              </div>
            )}
            
            {editingChangeOrder?.Status === 'sent' && (
              <div className="alert alert-info">
                <strong>Note:</strong> This change order has been sent for signature. 
                Changing the description or amount will invalidate the current signing link and generate a new one.
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveEdit}
            disabled={loadingAction === editingChangeOrder?.ChangeOrderId}
          >
            {loadingAction === editingChangeOrder?.ChangeOrderId ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChangeOrderList;
